import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { useForm } from 'react-hook-form';
import { InferType } from 'yup';

import getAddEditDutyCodeSchema from 'components/shared/DutyCodesGrid/AddDutyCode/utils/addDutyCodeSchema';
import getEditDutyCodeSchema from 'components/shared/DutyCodesGrid/EditPersonalizedDutyCode/utils/editPersonalizedDutyCodeSchema';

import ControlledValidFromValidTo from 'components/shared/ControlledFields/ControlledValidFromValidTo';
import DutyCodeBaseFields from 'components/shared/DutyCodesGrid/DutyCodeBaseFields';

import { mutations, queryKeys } from 'RestAPIs/rapini';
import { DutyCodeGeneralFields } from './GeneralDutyCodeFields';
import { convertToDutyCodeRequest } from '../utils/convertToDutyCodeRequest';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { ControlledTimePeriods } from 'components/shared/ControlledFields';
import dayjs from 'dayjs';
import { showSuccessSnackbar } from 'components/shared/Snackbar';
import { useQueryClient } from 'react-query';
import { showSnackbar } from 'components/shared/index';

export type DutyCodeForm = InferType<ReturnType<typeof getEditDutyCodeSchema>>;

export const EditGeneralDutyCodeForm = ({ handleClose, data }) => {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const { mutate: updateDutyCode, isLoading } = mutations.useUpdateDutyCode();
  const methods = useForm<DutyCodeForm>({
    defaultValues: {
      ...data,
      periods: data.periods
        .map((period) => ({
          dutyHourTypeId: period.dutyHourType?.dutyHourTypeId,
          startTime: dayjs(period.startTime),
          endTime: dayjs(period.endTime)
        }))
        .sort((a, b) => a.startTime.diff(b.startTime))
    },
    resolver: yupResolver(getAddEditDutyCodeSchema())
  });

  const { control, watch, handleSubmit } = methods;

  const onSubmit = (data) => {
    const editDutyCodeRequest = {
      ...convertToDutyCodeRequest(data),
      dutyCodeId: data.id
    };
    updateDutyCode(editDutyCodeRequest, {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKeys.getDutyCodes.name);
        showSuccessSnackbar();
        handleClose();
      },
      onError: (error: any) => {
        const message = t(
          error?.response?.data?.error?.message || 'somethingWentWrong'
        );
        showSnackbar({
          severity: 'error',
          message: message
        });
      }
    });
  };

  return (
    <MultiStepForm onSubmit={handleSubmit(onSubmit)} methods={methods}>
      <MultiStepForm.Section
        title={t('basis')}
        validateFields={['displayDutyCode', 'validFrom', 'validTo']}
      >
        <DutyCodeBaseFields />
        <DutyCodeGeneralFields />
        <ControlledValidFromValidTo
          control={control}
          validFromFieldName="validFrom"
          validToFieldName="validTo"
        />
      </MultiStepForm.Section>
      <MultiStepForm.Section title={t('periods')} validateFields={['periods']}>
        <ControlledTimePeriods
          control={control}
          watch={watch}
          hasDHTSelector
          hideBreakDuration
          disabled={!data.periodsCanBeEdited}
        />
      </MultiStepForm.Section>
      <MultiStepForm.Actions>
        <LoadingButton
          type="submit"
          variant="contained"
          autoFocus
          color="primary"
          loading={isLoading}
        >
          {t('save')}
        </LoadingButton>
      </MultiStepForm.Actions>
    </MultiStepForm>
  );
};
